import { formatDistanceToNow, differenceInMinutes, differenceInHours, differenceInDays, differenceInMonths, differenceInYears, parseISO, format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { enUS } from "date-fns/locale";

/**
 * Get a short time ago format (e.g., "5m", "2h", "3d", "1mo", "1y")
 */
export const getTimeAgoShort = (dateInput: Date | string): string => {
  const date = typeof dateInput === "string" ? parseISO(dateInput) : dateInput;
  const now = new Date();

  const diffMinutes = differenceInMinutes(now, date);
  const diffHours = differenceInHours(now, date);
  const diffDays = differenceInDays(now, date);
  const diffMonths = differenceInMonths(now, date);
  const diffYears = differenceInYears(now, date);

  if (diffMinutes < 60) {
    return `${diffMinutes}m`;
  } else if (diffHours < 24) {
    return `${diffHours}h`;
  } else if (diffDays < 30) {
    return `${diffDays}d`;
  } else if (diffMonths < 12) {
    return `${diffMonths}mo`;
  } else {
    return `${diffYears}y`;
  }
};

/**
 * Get relative time (e.g., "2 hours ago", "3 days ago")
 */
export const getTimeAgo = (dateInput: Date | string): string => {
  const date = typeof dateInput === "string" ? parseISO(dateInput) : dateInput;
  return formatDistanceToNow(date, { addSuffix: true });
};

/**
 * Format date with pattern (e.g., "MMM dd, yyyy")
 */
      export const formatDate = (
        dateInput: string | number | Date,
        locale = "en",
        options?: Intl.DateTimeFormatOptions
      ) => {
        const date =
          typeof dateInput === "string" ? new Date(dateInput) : dateInput;

        return new Intl.DateTimeFormat(
          locale === "te" ? "te-IN" : "en-US",
          options || {
            year: "numeric",
            month: "long",
            day: "2-digit",
          }
        ).format(date);
      };

/**
 * Format date with timezone (e.g., "Asia/Kolkata")
 */
export const formatDateInTimezone = (
  dateInput: Date | string,
  timeZone: string,
  locale: string = "en"
): string => {
  const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;

  return new Intl.DateTimeFormat(locale === "te" ? "te-IN" : "en-US", {
    timeZone,
    year: "numeric",
    month: "long",
    day: "2-digit",
  }).format(date);
};

/**
 * Get relative time with timezone awareness
 */
export const getTimeAgoInTimezone = (dateInput: Date | string, timeZone: string): string => {
  const date = typeof dateInput === "string" ? parseISO(dateInput) : dateInput;
  return formatDistanceToNow(date, { addSuffix: true });
};

/**
 * Format time ago with localization support (English and Telugu)
 */
export const formatTimeAgoLocalized = (dateInput: Date | string, language: "en" | "te" = "en"): string => {
  const date = typeof dateInput === "string" ? parseISO(dateInput) : dateInput;
  const now = new Date();

  const diffMinutes = differenceInMinutes(now, date);
  const diffHours = differenceInHours(now, date);
  const diffDays = differenceInDays(now, date);

  if (language === "te") {
    if (diffMinutes < 60) {
      return `${diffMinutes} ${diffMinutes === 1 ? "నిమిషం" : "నిమిషాల"} క్రితం`;
    } else if (diffHours < 24) {
      return `${diffHours} ${diffHours === 1 ? "గంట" : "గంటల"} క్రితం`;
    } else if (diffDays < 7) {
      return `${diffDays} ${diffDays === 1 ? "రోజు" : "రోజుల"} క్రితం`;
    } else {
      return new Intl.DateTimeFormat("te-IN", {
      year: "numeric",
      month: "long",
      day: "2-digit",
    }).format(date);
    }
  } else {
    if (diffMinutes < 60) {
      return `${diffMinutes} Min Ago`;
    } else if (diffHours < 24) {
      return `${diffHours} Hour${diffHours > 1 ? "s" : ""} Ago`;
    } else if (diffDays < 7) {
      return `${diffDays} Day${diffDays > 1 ? "s" : ""} Ago`;
    } else {
      return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "long",
      day: "2-digit",
    }).format(date);
    }
  }
};
